import { useEffect, useState } from "react";
import { api } from "@/hub/lib/api";
import { MailPlus, Send, Loader2, Paperclip, ImageIcon, AlertTriangle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-white border border-stone-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-forest";

export default function EmailCenter() {
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("individual");
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);

  const [ind, setInd] = useState({ to: "", name: "", subject: "", message: "", link_url: "https://www.lushadventure.com" });
  const [indFlyer, setIndFlyer] = useState(null);
  const [indFile, setIndFile] = useState(null);

  const [camp, setCamp] = useState({ name: "", subject: "", message: "", link_url: "https://www.lushadventure.com", tag: "all" });
  const [campFlyer, setCampFlyer] = useState(null);
  const [audience, setAudience] = useState(0);

  useEffect(() => {
    api.emailStatus().then(setStatus).catch(() => setStatus({ configured: false }));
    api.contacts("", "").then((d) => setContacts(d.contacts));
  }, []);

  useEffect(() => { api.campaignAudience(camp.tag).then((d) => setAudience(d.count)); }, [camp.tag]);

  const uploadFlyer = async (file) => {
    const r = await api.uploadFile(file);
    return r.url;
  };

  const sendIndividual = async () => {
    if (!ind.to || !ind.subject || !ind.message) return toast.error("Recipient, subject and message are required");
    setBusy(true);
    try {
      let flyer_url = "";
      if (indFlyer) flyer_url = await uploadFlyer(indFlyer);
      const fd = new FormData();
      fd.append("to", ind.to); fd.append("name", ind.name); fd.append("subject", ind.subject);
      fd.append("message", ind.message); fd.append("flyer_url", flyer_url); fd.append("link_url", ind.link_url);
      if (indFile) fd.append("attachment", indFile);
      await api.sendIndividual(fd);
      toast.success(`Sent to ${ind.to}`);
      setInd({ to: "", name: "", subject: "", message: "", link_url: "https://www.lushadventure.com" });
      setIndFlyer(null); setIndFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const sendCampaign = async () => {
    if (!camp.name || !camp.subject || !camp.message) return toast.error("Name, subject and message are required");
    if (!window.confirm(`Send this campaign to ${audience} contacts?`)) return;
    setBusy(true);
    try {
      let flyer_url = "";
      if (campFlyer) flyer_url = await uploadFlyer(campFlyer);
      const r = await api.sendCampaign({ ...camp, flyer_url });
      toast.success(`Campaign sent to ${r.recipients} contacts`);
      setCamp({ name: "", subject: "", message: "", link_url: "https://www.lushadventure.com", tag: "all" });
      setCampFlyer(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Campaign failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="email-center-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-forest flex items-center gap-2"><MailPlus className="h-6 w-6 text-terracotta" /> Email Center</h1>
        <p className="text-sm text-stone-500">Send official proposals to individuals and promotional campaigns to your contact list — from {status?.sender_email || "info@lushadventure.com"}.</p>
      </div>

      {status && !status.configured && (
        <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="email-not-configured">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Email sending not connected yet.</strong> Compose and prepare everything here — sending activates the moment the Brevo API key is added (setup steps shared separately).
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6" data-testid="email-tabs">
        {[["individual", "Individual / Proposal"], ["campaign", "Bulk Campaign"]].map(([k, l]) => (
          <button key={k} data-testid={`email-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider rounded-sm border transition-colors ${tab === k ? "bg-forest text-white border-forest" : "bg-white text-stone-500 border-stone-200 hover:border-forest"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "individual" ? (
        <div className="bg-white border border-stone-200 rounded-sm p-6 max-w-2xl space-y-4" data-testid="individual-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">To (email) *</label>
              <input data-testid="ind-to" list="contact-emails" className={inputCls} placeholder="client@company.com"
                value={ind.to} onChange={(e) => {
                  const v = e.target.value;
                  const hit = contacts.find((c) => c.email === v);
                  setInd({ ...ind, to: v, name: hit ? hit.name : ind.name });
                }} />
              <datalist id="contact-emails">{contacts.map((c) => <option key={c.id} value={c.email}>{c.name}</option>)}</datalist>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Recipient Name</label>
              <input data-testid="ind-name" className={inputCls} value={ind.name} onChange={(e) => setInd({ ...ind, name: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Subject *</label>
            <input data-testid="ind-subject" className={inputCls} placeholder="Your Lush Adventure proposal" value={ind.subject} onChange={(e) => setInd({ ...ind, subject: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Message *</label>
            <textarea data-testid="ind-message" rows={6} className={inputCls} placeholder={"Dear Client,\n\nThank you for your interest..."} value={ind.message} onChange={(e) => setInd({ ...ind, message: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-sm px-3 py-3 text-xs text-stone-500 cursor-pointer hover:border-forest" data-testid="ind-flyer-label">
              <ImageIcon className="h-4 w-4 text-terracotta" /> {indFlyer ? indFlyer.name : "Attach flyer image (optional)"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setIndFlyer(e.target.files[0] || null)} />
            </label>
            <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-sm px-3 py-3 text-xs text-stone-500 cursor-pointer hover:border-forest" data-testid="ind-file-label">
              <Paperclip className="h-4 w-4 text-terracotta" /> {indFile ? indFile.name : "Attach proposal PDF/DOC (max 5MB)"}
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => {
                const f = e.target.files[0];
                if (f && f.size > 5 * 1024 * 1024) return toast.error("File must be under 5 MB");
                setIndFile(f || null);
              }} />
            </label>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Website Link</label>
            <input data-testid="ind-link" className={inputCls} value={ind.link_url} onChange={(e) => setInd({ ...ind, link_url: e.target.value })} />
          </div>
          <Button data-testid="ind-send" onClick={sendIndividual} disabled={busy} className="rounded-sm bg-forest hover:bg-forest-hover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />} Send Official Email
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-6 max-w-2xl space-y-4" data-testid="campaign-form">
          <div className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-sm p-3 text-sm" data-testid="campaign-audience">
            <Users className="h-4 w-4 text-forest" />
            <select data-testid="campaign-tag" className="bg-transparent text-sm focus:outline-none" value={camp.tag} onChange={(e) => setCamp({ ...camp, tag: e.target.value })}>
              <option value="all">All mailable contacts</option>
              <option value="professional">Professionals only</option>
              <option value="individual">Individuals only</option>
            </select>
            <span className="text-forest font-semibold">{audience} recipients</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Campaign Name *</label>
              <input data-testid="camp-name" className={inputCls} placeholder="October Corporate Promo" value={camp.name} onChange={(e) => setCamp({ ...camp, name: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Subject *</label>
              <input data-testid="camp-subject" className={inputCls} placeholder="Team building season is here" value={camp.subject} onChange={(e) => setCamp({ ...camp, subject: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Message *</label>
            <textarea data-testid="camp-message" rows={6} className={inputCls} placeholder="Write your promotional message..." value={camp.message} onChange={(e) => setCamp({ ...camp, message: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-sm px-3 py-3 text-xs text-stone-500 cursor-pointer hover:border-forest" data-testid="camp-flyer-label">
            <ImageIcon className="h-4 w-4 text-terracotta" /> {campFlyer ? campFlyer.name : "Attach flyer image (optional)"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setCampFlyer(e.target.files[0] || null)} />
          </label>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1.5">Website Link</label>
            <input data-testid="camp-link" className={inputCls} value={camp.link_url} onChange={(e) => setCamp({ ...camp, link_url: e.target.value })} />
          </div>
          <p className="text-[11px] text-stone-400">Campaigns include an automatic unsubscribe footer. Attachments are not sent in bulk campaigns — use the website link instead.</p>
          <Button data-testid="camp-send" onClick={sendCampaign} disabled={busy || audience === 0} className="rounded-sm bg-terracotta hover:bg-terracotta-hover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />} Send to {audience} Contacts
          </Button>
        </div>
      )}
    </div>
  );
}
